import React, { useState, useEffect, useMemo } from "react";
import Modal from "react-modal";
import moment from "moment";
import { MdClose, MdPayment, MdAccountBalanceWallet, MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { FaCreditCard } from "react-icons/fa";
import "./style.css";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    maxWidth: "580px",
    width: "90%",
    maxHeight: "85vh",
    padding: "0",
    borderRadius: "12px",
    border: "none",
    boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.25)",
    overflow: "hidden",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1000,
  },
};

Modal.setAppElement("#root");

// Calculate importer price from finalPrice or FBP breakdown
const calculateImporterrPrice = (order) => {
  const hasWholesaleBreakdown = Array.isArray(order?.breakdown) && 
    order.breakdown.length > 0 && 
    order.breakdown[0]?.breakdown?.[0]?.FBP;

  if (hasWholesaleBreakdown) {
    const mkpPrice = Number(order?.mkp_current_total_price);
    return Number.isFinite(mkpPrice) && mkpPrice > 0 ? mkpPrice : 0;
  }

  if (!Array.isArray(order?.orderDetails)) {
    const fallback = Number(order?.totalPrice) ?? Number(order?.totalConsumerPrice) ?? 0;
    return Number.isFinite(fallback) ? fallback : 0;
  }

  // Helper function to get price - prioritize finalPrice over breakdown.FBP
  const getItemPrice = (detail) => {
    const variant = detail?.variant;
    // Priority: finalPrice > breakdown.FBP
    if (variant?.finalPrice != null && Number(variant.finalPrice) > 0) {
      return Number(variant.finalPrice);
    }
    const breakdown = variant?.breakdown;
    return Number(breakdown?.FBP ?? breakdown?.fbp) || 0;
  };

  let hasPrice = false;
  const total = order.orderDetails.reduce((sum, detail) => {
    const price = getItemPrice(detail);

    if (!Number.isFinite(price) || price === 0) {
      return sum;
    }

    hasPrice = true;
    const quantity = Number(detail?.quantity);
    const validQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    return sum + price * validQuantity;
  }, 0);

  if (hasPrice) {
    return total;
  }

  const fallback = Number(order?.totalPrice) ?? Number(order?.totalConsumerPrice);
  if (Number.isFinite(fallback)) {
    return fallback;
  }

  return order.orderDetails.reduce((acc, detail) => {
    const consumerPrice = Number(detail?.consumerPrice);
    return acc + (Number.isFinite(consumerPrice) ? consumerPrice : 0);
  }, 0);
};

const PaymentModal = ({ isOpen, onClose, order, orders, onConfirm, isLoading, walletBalance = 0 }) => {
  const [paymentMethod, setPaymentMethod] = useState("wallet");
  
  // Support both single order and multiple orders
  const initialOrderList = useMemo(() => {
    return orders && orders.length > 0 ? orders : (order ? [order] : []);
  }, [orders, order]);
  
  const isMultiple = initialOrderList.length > 1;
  
  // State for selected orders (for multiple orders)
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  
  // Initialize selected orders when modal opens
  useEffect(() => {
    if (isOpen && initialOrderList.length > 0) {
      setSelectedOrderIds(initialOrderList.map(ord => ord._id));
    }
  }, [isOpen, initialOrderList]);

  if (initialOrderList.length === 0) return null;

  // Get currently selected orders
  const selectedOrders = initialOrderList.filter(ord => selectedOrderIds.includes(ord._id));

  // Calculate price for a single order
  const calculateOrderPrice = (ord) => {
    if (!ord) return { productTotal: 0, shippingCharges: 0, total: 0 };

    const productTotal = calculateImporterrPrice(ord);
    const shippingCharges = Number(ord?.totalShippingCharges) || 0;
    
    return {
      productTotal,
      shippingCharges,
      total: productTotal + shippingCharges,
    };
  };

  // Calculate totals for selected orders only
  const calculateTotals = () => {
    let totalProduct = 0;
    let totalShipping = 0;

    selectedOrders.forEach((ord) => {
      const prices = calculateOrderPrice(ord);
      totalProduct += prices.productTotal;
      totalShipping += prices.shippingCharges;
    });

    return {
      productTotal: totalProduct,
      shippingCharges: totalShipping,
      total: totalProduct + totalShipping,
    };
  };

  const totals = calculateTotals();
  const hasInsufficientBalance = walletBalance < totals.total;
  const afterPaymentBalance = walletBalance - totals.total;

  const formatCurrency = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "₹0.00";
    return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Toggle order selection
  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  // Select/Deselect all orders
  const toggleSelectAll = () => {
    if (selectedOrderIds.length === initialOrderList.length) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(initialOrderList.map(ord => ord._id));
    }
  };

  const handleConfirm = () => {
    if (selectedOrders.length === 0) return;
    const orderIds = selectedOrders.map((ord) => ord._id);
    onConfirm(orderIds, paymentMethod);
  };

  const isAllSelected = selectedOrderIds.length === initialOrderList.length;

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={customStyles} contentLabel="Pay Order">
      <div className="payment-modal">
        {/* Header */}
        <div className="payment-modal-header">
          <div className="header-icon">
            <MdPayment />
          </div>
          <h2>{isMultiple ? `Pay for Orders` : "Pay for Order"}</h2>
          <button className="close-btn" onClick={onClose}>
            <MdClose />
          </button>
        </div>

        {/* Body */}
        <div className="payment-modal-body">
          {/* Single Order Details */}
          {!isMultiple && initialOrderList[0] && (
            <div className="order-info-section">
              <div className="info-row">
                <span className="label">Order ID:</span>
                <span className="value">{initialOrderList[0].upsaleoOrderId || initialOrderList[0].orderId}</span>
              </div>
              <div className="info-row">
                <span className="label">MKP Order ID:</span>
                <span className="value">{initialOrderList[0].orderId}</span>
              </div>
              <div className="info-row">
                <span className="label">Order Date:</span>
                <span className="value">{moment(initialOrderList[0].orderDate).format("Do MMM YYYY, LT")}</span>
              </div>
              <div className="info-row">
                <span className="label">Status:</span>
                <span className={`status-badge status-${initialOrderList[0].status?.toLowerCase().replace(' ', '-')}`}>
                  {initialOrderList[0].status}
                </span>
              </div>
            </div>
          )}

          {/* Multiple Orders List with Selection */}
          {isMultiple && (
            <div className="orders-list-section">
              <div className="orders-list-header">
                <h3>Select Orders to Pay ({selectedOrders.length} of {initialOrderList.length} selected)</h3>
                <button 
                  type="button" 
                  className="select-all-btn"
                  onClick={toggleSelectAll}
                >
                  {isAllSelected ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="orders-list">
                {initialOrderList.map((ord, index) => {
                  const prices = calculateOrderPrice(ord);
                  const isSelected = selectedOrderIds.includes(ord._id);
                  return (
                    <div 
                      key={ord._id || index} 
                      className={`order-item selectable ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleOrderSelection(ord._id)}
                    >
                      <div className="order-checkbox">
                        {isSelected ? (
                          <MdCheckBox className="checkbox-icon checked" />
                        ) : (
                          <MdCheckBoxOutlineBlank className="checkbox-icon" />
                        )}
                      </div>
                      <div className="order-item-content">
                        <div className="order-item-header">
                          <span className="order-id">{ord.upsaleoOrderId || ord.orderId}</span>
                          <span className={`status-badge status-${ord.status?.toLowerCase().replace(' ', '-')}`}>
                            {ord.status}
                          </span>
                        </div>
                        <div className="order-item-details">
                          <span>Product: {formatCurrency(prices.productTotal)}</span>
                          {prices.shippingCharges > 0 && (
                            <span>Shipping: {formatCurrency(prices.shippingCharges)}</span>
                          )}
                          <span className="order-total">Total: {formatCurrency(prices.total)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Price Breakdown */}
          <div className="price-section">
            <h3>Payment Details {isMultiple && selectedOrders.length > 0 ? `(${selectedOrders.length} orders)` : ''}</h3>
            {selectedOrders.length === 0 ? (
              <div className="no-orders-selected">
                <p>Please select at least one order to pay</p>
              </div>
            ) : (
              <>
                <div className="price-row">
                  <span>Product Total:</span>
                  <span>{formatCurrency(totals.productTotal)}</span>
                </div>
                {totals.shippingCharges > 0 && (
                  <div className="price-row">
                    <span>Shipping Charges:</span>
                    <span>{formatCurrency(totals.shippingCharges)}</span>
                  </div>
                )}
                <div className="price-row total">
                  <span>Total Amount:</span>
                  <span className="payment-amount">{formatCurrency(totals.total)}</span>
                </div>
              </>
            )}
          </div>

          {/* Payment Method Selection */}
          {selectedOrders.length > 0 && (
            <div className="payment-method-section">
              <h3>Select Payment Method</h3>
              <div className="payment-methods">
                <label 
                  className={`payment-method-option ${paymentMethod === 'wallet' ? 'selected' : ''} ${hasInsufficientBalance ? 'insufficient' : ''}`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="wallet"
                    checked={paymentMethod === 'wallet'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <div className="method-content">
                    <MdAccountBalanceWallet className="method-icon" />
                    <div className="method-details">
                      <span className="method-name">IM Cash</span>
                      <span className="method-balance">Current Balance: {formatCurrency(walletBalance)}</span>
                      <span className={`method-after-balance ${afterPaymentBalance < 0 ? 'negative' : ''}`}>
                        After Payment: {formatCurrency(afterPaymentBalance)}
                      </span>
                      {hasInsufficientBalance && (
                        <span className="insufficient-warning">Insufficient balance</span>
                      )}
                    </div>
                  </div>
                </label>
                
                <label 
                  className={`payment-method-option ${paymentMethod === 'razorpay' ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="razorpay"
                    checked={paymentMethod === 'razorpay'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <div className="method-content">
                    <FaCreditCard className="method-icon" />
                    <div className="method-details">
                      <span className="method-name">Razorpay</span>
                      <span className="method-desc">Credit/Debit Card, UPI, Net Banking</span>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="payment-modal-footer">
          <button className="cancel-btn" onClick={onClose} disabled={isLoading}>
            Cancel
          </button>
          <button 
            className="confirm-btn" 
            onClick={handleConfirm} 
            disabled={isLoading || selectedOrders.length === 0 || (paymentMethod === 'wallet' && hasInsufficientBalance)}
          >
            {isLoading ? "Processing..." : selectedOrders.length === 0 ? "Select Orders" : `Pay ${formatCurrency(totals.total)}`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PaymentModal;
